import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Checkbox,
  Divider,
  Tooltip,
  Collapse,
  message,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import IncredableButton from "../../../components/button";
import { Attribute } from "../../../redux/api/section/types";
import { TypeOptions } from "../add-section";
import { typeFields } from "./add-attribute-modal";
import { useDeleteAttributeOptionMutation } from "../../../redux/api/section/section";
import { SpinnerContext } from "../../../context/spinner/spinner";
import { Countries, USStatesList } from "../../../common/constants";
import EditableOptionTable, { OptionItem } from "./editable-option-table";
import { SpaceBetween } from "@cloudscape-design/components";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const SectionTitle = ({ title }: { title: string }) => (
  <Title level={5} style={{ marginTop: 24 }}>
    {title}
  </Title>
);

interface EditAttributeModalProps {
  isEditFieldModalOpen: boolean;
  setIsEditFieldModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleEditAttribute: () => void;
  editAttribute: Attribute;
  setEditAttribute: React.Dispatch<React.SetStateAction<Attribute | null>>;
  attributeType: string;
  sectionId?: string;
}

const EditAttributeModal: React.FC<EditAttributeModalProps> = ({
  isEditFieldModalOpen,
  setIsEditFieldModalOpen,
  handleEditAttribute,
  editAttribute,
  setEditAttribute,
  attributeType,
  sectionId,
}) => {
  const spinnerContext = useContext(SpinnerContext);
  const [deleteAttributeOption] = useDeleteAttributeOptionMutation();
  const [selectedStateValues, setSelectedStateValues] = useState<string[]>([]);
  const [selectedCountryValues, setSelectedCountryValues] = useState<string[]>(
    []
  );

  const showError = (msg: string) => message.error(msg);

  const handleStateChange = useCallback((values: string[]) => {
    setSelectedStateValues(values);
    updateOptionDefinition(values);
  }, []);

  const handleCountryChange = useCallback((values: string[]) => {
    setSelectedCountryValues(values);
    updateOptionDefinition(values);
  }, []);

  const updateOptionDefinition = (values: string[]) => {
    setEditAttribute((prev) => {
      if (!prev) return prev;
      const existingMap = new Map(
        (prev.optionDefinition || []).map((opt) => [opt.value, opt])
      );
      const updated = values.map(
        (val, i) => existingMap.get(val) || { label: val, value: val }
      );
      return { ...prev, optionDefinition: updated };
    });
  };

  useEffect(() => {
    if (!editAttribute) return;
    if (editAttribute?.type === "States") {
      const defaults =
        editAttribute?.optionDefinition?.map((opt) => opt.value) || [];
      setSelectedStateValues(defaults);
    }
    if (editAttribute?.type === "Countries") {
      const defaults =
        editAttribute?.optionDefinition?.map((opt) => opt.value) || [];
      setSelectedCountryValues(defaults);
    }
  }, [isEditFieldModalOpen]);

  const renderTypeSpecificFields = () => {
    if (
      editAttribute?.type === "Dropdown.Single" ||
      editAttribute?.type === "Dropdown.Multiple"
    ) {
      return (
        <>
          <SectionTitle title="Field Options" />
          <EditableOptionTable
            options={(editAttribute?.optionDefinition as OptionItem[]) || []}
            setOptions={(opts) =>
              setEditAttribute({ ...editAttribute, optionDefinition: opts })
            }
          />
        </>
      );
    }
    if (editAttribute?.type === "States") {
      return (
        <Select
          mode="multiple"
          allowClear
          value={selectedStateValues}
          style={{ width: "100%" }}
          placeholder="Select states"
          onChange={handleStateChange}
          options={USStatesList.map((opt) => ({
            label: opt.value,
            value: opt.value,
          }))}
        />
      );
    }
    if (editAttribute?.type === "Countries") {
      return (
        <Select
          mode="multiple"
          allowClear
          value={selectedCountryValues}
          style={{ width: "100%" }}
          placeholder="Select countries"
          onChange={handleCountryChange}
          options={Countries.map((opt) => ({
            label: opt.value,
            value: opt.value,
          }))}
        />
      );
    }
    return null;
  };

  return (
    <Modal
      title={<Title level={4}>Edit Field</Title>}
      open={isEditFieldModalOpen}
      onCancel={() => setIsEditFieldModalOpen(false)}
      footer={null}
      width="80vw"
      style={{ top: 50 }}
      bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
    >
      <Form layout="vertical">
        <Card bordered={false}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Field Type">
                <Select
                  disabled
                  value={editAttribute?.type}
                  options={TypeOptions}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label={
                  <Text strong>
                    Field Name <Text type="danger">*</Text>
                  </Text>
                }
              >
                <Input
                  value={editAttribute?.name}
                  onChange={(e) =>
                    setEditAttribute({ ...editAttribute, name: e.target.value })
                  }
                  autoFocus
                />
              </Form.Item>
            </Col>
          </Row>

          {editAttribute?.type !== "Divider" && (
            <Form.Item label="Hint Text">
              <Input
                placeholder="Hint text (optional)"
                value={editAttribute?.description}
                onChange={(e) =>
                  setEditAttribute({
                    ...editAttribute,
                    description: e.target.value,
                  })
                }
              />
            </Form.Item>
          )}

          <SectionTitle title="Behavior Settings" />
          <Row gutter={16}>
            <Col span={8}>
              <Checkbox
                checked={!!editAttribute?.required}
                onChange={(e) =>
                  setEditAttribute({
                    ...editAttribute,
                    required: e.target.checked,
                  })
                }
              >
                Required Field
              </Checkbox>
            </Col>
            <Col span={8}>
              <Checkbox
                checked={!!editAttribute?.sensitive}
                onChange={(e) =>
                  setEditAttribute({
                    ...editAttribute,
                    sensitive: e.target.checked,
                  })
                }
              >
                Sensitive Info{" "}
                <Tooltip title="E.g., SSN or ID. Access limited by role.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Checkbox>
            </Col>
            <Col span={8}>
              <SpaceBetween direction="horizontal" size="s">
                <span>Column Span</span>
                <Input
                  variant="underlined"
                  value={editAttribute?.colSpan}
                  onChange={(e) =>
                    setEditAttribute({
                      ...editAttribute,
                      colSpan: e.target.value,
                    })
                  }
                  autoFocus
                />
              </SpaceBetween>
            </Col>
          </Row>

          {editAttribute?.type !== "Divider" && (
            <>{renderTypeSpecificFields()}</>
          )}

          <SectionTitle title="Preview" />
          <Card bordered style={{ marginTop: 12 }}>
            <Form.Item
              label={
                editAttribute?.required
                  ? `${editAttribute?.name} *`
                  : editAttribute?.name
              }
            >
              {editAttribute && typeFields(editAttribute.type, editAttribute)}
            </Form.Item>
          </Card>

          <Divider />

          <Row justify="end">
            <Space>
              <Button onClick={() => setIsEditFieldModalOpen(false)}>
                Cancel
              </Button>
              <IncredableButton type="primary" onClick={handleEditAttribute}>
                Save Changes
              </IncredableButton>
            </Space>
          </Row>
        </Card>
      </Form>
    </Modal>
  );
};

export default EditAttributeModal;
